import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { IconButton } from '@atlaskit/button/new';
import LikeIcon from '@atlaskit/icon/glyph/like';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline } from '@atlaskit/primitives';
import { N100 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';
import messages from './messages';
import { buttonGroupStyles, dislikeButtonStyles } from './styles';
var FeedbackLikeIcon = function FeedbackLikeIcon(_ref) {
  var label = _ref.label,
    iconColor = _ref.iconColor;
  return jsx(LikeIcon, {
    label: label,
    primaryColor: iconColor
  });
};
var hideBackgroundStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  button: {
    backgroundColor: "var(--ds-background-neutral-subtle, #00000000)"
  }
});
var Announcer = function Announcer(_ref2) {
  var message = _ref2.message,
    shouldAnnounce = _ref2.shouldAnnounce;
  return jsx(VisuallyHidden, null, jsx(Box, {
    "aria-atomic": "true",
    "aria-live": "polite"
  }, shouldAnnounce && message));
};
var LikeIconButton = function LikeIconButton(_ref3) {
  var onClick = _ref3.onClick,
    isPressed = _ref3.isPressed,
    isDisabled = _ref3.isDisabled,
    pressedColor = _ref3.pressedColor,
    iconColor = _ref3.iconColor;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var goodResponseLabel = formatMessage(messages.goodResponse);
  var Icon = function Icon(props) {
    return jsx(FeedbackLikeIcon, _extends({}, props, {
      iconColor: isPressed ? pressedColor : iconColor
    }));
  };
  return jsx("div", {
    css: [(isPressed || isDisabled) && hideBackgroundStyles]
  }, jsx(Tooltip, {
    content: goodResponseLabel,
    position: "top"
  }, jsx(IconButton, {
    testId: "good-response-feedback-button",
    type: "button",
    isDisabled: isDisabled,
    onClick: onClick,
    icon: Icon,
    label: goodResponseLabel,
    appearance: "subtle",
    spacing: "compact"
  })));
};
var DislikeIconButton = function DislikeIconButton(_ref4) {
  var onClick = _ref4.onClick,
    isPressed = _ref4.isPressed,
    isDisabled = _ref4.isDisabled,
    pressedColor = _ref4.pressedColor,
    iconColor = _ref4.iconColor;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var badResponseLabel = formatMessage(messages.badResponse);
  var Icon = function Icon(props) {
    return jsx(FeedbackLikeIcon, _extends({}, props, {
      iconColor: isPressed ? pressedColor : iconColor
    }));
  };
  return (
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    jsx("div", {
      css: [dislikeButtonStyles, (isPressed || isDisabled) && hideBackgroundStyles]
    }, jsx(Tooltip, {
      content: badResponseLabel,
      position: "top"
    }, jsx(IconButton, {
      testId: "bad-response-feedback-button",
      type: "button",
      isDisabled: isDisabled,
      onClick: onClick,
      icon: Icon,
      label: badResponseLabel,
      appearance: "subtle",
      spacing: "compact"
    })))
  );
};
export var FeedbackForm = function FeedbackForm(_ref5) {
  var onGoodResponseButtonClick = _ref5.onGoodResponseButtonClick,
    onBadResponseButtonClick = _ref5.onBadResponseButtonClick,
    _ref5$iconsOnly = _ref5.iconsOnly,
    iconsOnly = _ref5$iconsOnly === void 0 ? false : _ref5$iconsOnly,
    pressedIconsColor = _ref5.pressedIconsColor;
  var pressedColor = pressedIconsColor !== null && pressedIconsColor !== void 0 ? pressedIconsColor : "var(--ds-interaction-pressed, #00000052)";
  var _useIntl3 = useIntl(),
    formatMessage = _useIntl3.formatMessage;
  var feedbackWasSentLabel = formatMessage(messages.feedbackSent);
  var _React$useState = React.useState(),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    feedbackSelected = _React$useState2[0],
    setFeedbackSelected = _React$useState2[1];
  var handleOnClick = React.useCallback(function (onClick, feedback) {
    return function () {
      onClick();
      setFeedbackSelected(feedback);
    };
  }, [setFeedbackSelected]);
  if (iconsOnly) {
    return jsx(React.Fragment, null, jsx(Announcer, {
      message: feedbackWasSentLabel,
      shouldAnnounce: Boolean(feedbackSelected)
    }), jsx(LikeIconButton, {
      isPressed: feedbackSelected === 'good',
      isDisabled: Boolean(feedbackSelected),
      pressedColor: pressedColor,
      onClick: handleOnClick(onGoodResponseButtonClick, 'good')
    }), jsx(DislikeIconButton, {
      isPressed: feedbackSelected === 'bad',
      isDisabled: Boolean(feedbackSelected),
      pressedColor: pressedColor,
      onClick: handleOnClick(onBadResponseButtonClick, 'bad')
    }));
  }
  return (
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    jsx("div", {
      css: buttonGroupStyles
    }, feedbackSelected && jsx("p", {
      "aria-atomic": "false",
      "aria-live": "polite"
    }, jsx(Lozenge, {
      appearance: "success",
      testId: "feedback-success"
    }, feedbackWasSentLabel)), jsx(Inline, {
      space: "space.100"
    }, jsx(LikeIconButton, {
      iconColor: "var(--ds-icon, ".concat(N100, ")"),
      isDisabled: Boolean(feedbackSelected),
      onClick: handleOnClick(onGoodResponseButtonClick, 'good')
    }), jsx(DislikeIconButton, {
      iconColor: "var(--ds-icon, ".concat(N100, ")"),
      isDisabled: Boolean(feedbackSelected),
      onClick: handleOnClick(onBadResponseButtonClick, 'bad')
    })))
  );
};